import { RuntimeMediaQuery } from '@duda-co/responsive-styles/RuntimeMediaQuery';
import { ResponsiveStylesStructured } from '@duda-co/responsive-styles/types/ResponsiveStylesTypes';
import { Div } from 'client/widget-components/basicComponents';
import { Customization } from 'client/widget-components/ecom/stores/types';
import { mergeResponsiveStyles } from 'client/widget-components/responsive-styles/responsiveStylesService';
import React, { ChangeEventHandler, FC, memo, useCallback } from 'react';
import { CUSTOMIZATIONS_DATA_GRABS } from '../../constants';
import { ProductCustomizationsStyles } from '../../types';
import BaseProductCustomizationLabel from '../BaseProductCustomizationLabel';
import CustomizationFieldError from '../CustomizationFieldError';
import CheckboxCustomizationChoices from './CheckboxCustomizationChoices';

interface CheckboxCustomizationProps extends Customization {
    styles?: ProductCustomizationsStyles;
    value?: string[];
    error?: string | null;
    onChange: (fieldId: string, value: string[]) => void;
    validateField: (fieldId: string) => void;
}

const CheckboxCustomization: FC<CheckboxCustomizationProps> = ({
    id,
    label,
    mandatory = false,
    choices,
    error,
    limitations,
    value = [],
    styles,
    onChange,
}) => {
    const handleChange: ChangeEventHandler<HTMLInputElement> = useCallback(
        (e) => {
            const { checked, name } = e.target;

            if (checked) {
                onChange(id, [...value, name]);
            } else {
                onChange(
                    id,
                    value.filter((v) => v !== name)
                );
            }
        },
        [onChange, id, value]
    );

    return (
        <Div
            data-auto='product-customizations-checkbox'
            data-grab={CUSTOMIZATIONS_DATA_GRABS.checkboxWrapperDataGrab}
            styles={styles?.checkboxWrapper}
        >
            <BaseProductCustomizationLabel
                label={label}
                required={mandatory}
                styles={mergeResponsiveStyles([
                    labelDefaultStyles,
                    styles?.checkboxLabel,
                ])}
                dataGrab={CUSTOMIZATIONS_DATA_GRABS.checkboxLabelDataGrab}
                dataAuto='product-customizations-checkbox-label'
            />
            <CheckboxCustomizationChoices
                choices={choices}
                limitations={limitations}
                value={value}
                styles={{
                    choicesContainer: styles?.choicesContainer,
                    choiceChecked: styles?.choiceChecked,
                    choice: styles?.choice,
                    choiceLabel: styles?.choiceLabel,
                }}
                onChange={handleChange}
            />

            {!!error && <CustomizationFieldError error={error} />}
        </Div>
    );
};

const labelDefaultStyles: ResponsiveStylesStructured = {
    [RuntimeMediaQuery.COMMON]: {
        marginBlockEnd: '8px',
    },
};

export default memo(CheckboxCustomization);
